import { Capacitor } from '@capacitor/core';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { isFirebaseConfigured } from './firebase/app';
import { SetupScreen } from './screens/SetupScreen';
import { CallProvider } from './store/CallContext';
import { SessionProvider } from './store/SessionContext';

import { setUpNativeKeyboard } from './native/keyboard';
import { installerOuvertures } from './native/notifications';
import { direLaLangueAuTelephone } from './native/langue';
import { preference } from './i18n';
import { readAmbiancePreference, resolveAmbiance } from './theme/ambiance';
import { limiterLaSelection } from './utils/selection';
import { applyServerTheme, DEFAULT_THEME } from './theme/applyTheme';

import './theme/fonts.css';
import './theme/tokens.css';
import './styles/components.css';
import './styles/auth.css';
import './styles/app.css';

/*
  L'heure s'applique avant le premier rendu : la session met un
  aller-retour a arriver, et sans cela un telephone en mode sombre verrait
  l'application s'ouvrir en blanc le temps de ce trajet.
*/
applyServerTheme(DEFAULT_THEME, resolveAmbiance(readAmbiancePreference()));

/*
  La plateforme, posee sur la racine comme l'est l'ambiance : la feuille
  de style peut alors ajuster ce que `env(safe-area-inset-*)` ne dit pas.
  Sur un Android a boutons de navigation, cette valeur vaut zero et la
  barre d'onglets se collait au bord — voir `--tabbar-gap`.
*/
document.documentElement.dataset.plateforme = Capacitor.getPlatform();

/*
  Le choix de langue, redit au telephone a chaque lancement : une coquille
  arrivee apres ce choix l'apprend ainsi sans qu'on ait a le refaire.
  « Automatique » ne se redit pas : il effacerait la langue que Reglages ▸
  Sirocco ▸ Langue a pu donner a l'application.
*/
if (preference !== 'auto') void direLaLangueAuTelephone(preference);

// Reglages du clavier natif : sans effet sur le web.
setUpNativeKeyboard();

// Un appui long selectionne un message, jamais ses voisins.
limiterLaSelection();

/*
  L'ecoute des notifications touchees, avant le premier rendu : toucher
  une notification peut lancer l'application, et l'evenement arrive alors
  avant que le routeur existe. Ce qui tombe trop tot est mis de cote.
*/
installerOuvertures();

/*
  Sans configuration Firebase, rien ne peut fonctionner : on affiche la
  marche a suivre plutot qu'une erreur de connexion incomprehensible.
*/
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {isFirebaseConfigured ? (
      <BrowserRouter>
        <SessionProvider>
          {/*
            L'appel vit au-dessus des ecrans : il leur survit quand on se
            promene pendant la conversation, et la banniere d'appel
            entrant doit pouvoir surgir quel que soit l'ecran ouvert.
          */}
          <CallProvider>
            <App />
          </CallProvider>
        </SessionProvider>
      </BrowserRouter>
    ) : (
      <div className="app-frame">
        <SetupScreen />
      </div>
    )}
  </StrictMode>,
);
